import { Box, Button, ButtonGroup, IconButton,  useMediaQuery } from '@mui/material';
import { GooglePlacesSearch, useModules } from '@schamburg-web/components';
import { ControlPosition, MapControl, useMap } from '@vis.gl/react-google-maps';
import React from 'react';
import { useLiveRegion } from '@schamburg-web/components';
import GooglePlacesSearchWrapper from './GooglePlacesSearchWrapper';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import MapIcon from '@mui/icons-material/Map';

// Lake County, IL bounds (approximate)
const LAKE_COUNTY_BOUNDS = {
  north: 42.5, // Northern boundary
  south: 42.0, // Southern boundary
  east: -87.5, // Eastern boundary
  west: -88.2, // Western boundary
};

interface MapControlProps {
  controlPosition: ControlPosition;
}

const MapTypeControl: React.FC<MapControlProps> = ({ controlPosition }) => {
  const map = useMap();
  const [type, setType] = React.useState<'roadmap' | 'satellite'>('roadmap');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { setMessage } = useLiveRegion();
  const { sidebarOpen } = useModules();

  React.useEffect(() => {
    if (map) {
      const currentType = map.getMapTypeId?.() || 'roadmap';
      setType(currentType as 'roadmap' | 'satellite');
    }
  }, [map]);

  const handleSetType = (newType: 'roadmap' | 'satellite') => {
    if (!map) return;
    map.setMapTypeId?.(newType);
    setType(newType);
    setMessage(
      newType === 'roadmap' ? 'Road map selected' : 'Satellite map selected'
    );
  };

  return (
    <MapControl position={controlPosition} >
      <Box
        className="map-control"
        role="group"
        aria-label="Map type selection" 
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'right', md: 'center' },
          gap: 1,
          flexWrap: 'wrap',
          minWidth:'220px',
          minHight:91,
          marginBottom: 0,
         }}
      >
        {!isMobile && (
         <GooglePlacesSearchWrapper
          restrictToViewBounds={false}
          countyBounds={LAKE_COUNTY_BOUNDS}
        />)}
        {isMobile && (
         <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",  
            height: 48,
          }}
        >
          <GooglePlacesSearchWrapper
            restrictToViewBounds={false}
            countyBounds={LAKE_COUNTY_BOUNDS}
          />
        </Box> )}
         {isMobile ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",  
            }}
            >
            <IconButton
                color="primary"
                onClick={() =>
                  handleSetType(type === 'satellite' ? 'roadmap' : 'satellite')
                }
                aria-label={`Switch to ${
                  type === 'satellite' ? 'road map' : 'satellite'
                } view`}
                sx={{
                  boxShadow: 2,
                  width: 50,   
                  height: 50,
                  flexShrink: 0,
                }}
                className='map-control-on map-control-icon-large'
              >
                {type === 'satellite' ? (
                  <MapIcon sx={{ fontSize: 24 }}  />
                ) : (
                  <SatelliteAltIcon sx={{ fontSize: 24 }} />
                )}
            </IconButton>
          </Box>
      ) : (
        <ButtonGroup
          variant="contained"
          size="small"
          sx={{
            flexGrow: 1,
            flexShrink: 0,
            minWidth: 'fit-content',
          }}
        >
          <Button
            sx={{
              height: '35px',
              flex: 1,
              outline: 'none',
              '&:focus-visible': {
                outline: '2px solid #1976d2',
                outlineOffset: '2px',
                borderRadius: '4px',
              },
            }}
            onClick={() => handleSetType('roadmap')}
            aria-label="Switch to road map view"
            aria-pressed={type === 'roadmap'}
            tabIndex={0}
            className={
              type === 'roadmap'
                ? 'map-control-button map-control-on '
                : 'map-control-button map-control-off '
            }
          >
            Road
          </Button>
          <Button
            sx={{
              flex: 1,
              outline: 'none',
              '&:focus-visible': {
                outline: '2px solid #1976d2',
                outlineOffset: '2px',
                borderRadius: '4px',
              },
            }}
            onClick={() => handleSetType('satellite')}
            aria-label="Switch to satellite map view"
            aria-pressed={type === 'satellite'}
            tabIndex={0}
            className={
              type === 'satellite'
                ? ' map-control-button map-control-on '
                : 'map-control-button map-control-off '
            }
          >
            Satellite
          </Button>
        </ButtonGroup>
        )}
         
      </Box>
    </MapControl>
  );
};

export default MapTypeControl;
